import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Box, Card, CardContent, makeStyles, Typography,
} from '@material-ui/core';
import WarningRounded from '@material-ui/icons/WarningRounded';

import Button from 'creative-components/CustomButtons/Button';

import DashboardSection from 'components/DashboardSection/DashboardSection';
import InvoicesTable from 'components/InvoicesTable/InvoicesTable';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import AddPaymentMethodButton from 'components/PaymentMethods/AddPaymentMethodButton';
import PaymentMethodDisplay from 'components/PaymentMethods/PaymentMethodDisplay';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { getUserInvoices } from 'utils/api';
import { formatCents } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%', flex: 1, display: 'flex', flexDirection: 'column',
  },
  overdue: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', margin: theme.spacing(2, 0), background: '#FFF3E0', border: '1px solid #F6C177',
  },
  overdueCopy: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), color: '#7A3500',
  },
  methodsHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', margin: '10px 0 30px',
  },
  methods: {
    marginTop: theme.spacing(2), marginBottom: theme.spacing(2), gap: theme.spacing(2), display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
  },
  intro: { color: theme.palette.text.secondary, maxWidth: 720, marginTop: theme.spacing(0.5) },
}));

const tabValues = ['payment-methods', 'invoices'];
const tabLabels = ['Payment Methods', 'Invoices'];

const UserBillingPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { userInfo } = useUserInfoContext();
  const params = new URLSearchParams(location.search);
  const requestedTab = params.get('tab');
  const activeTab = tabValues.includes(requestedTab) ? requestedTab : 'payment-methods';
  const [invoices, setInvoices] = React.useState();

  const loadInvoices = React.useCallback(async () => {
    const result = await getUserInvoices();
    setInvoices(result.invoices);
  }, []);

  const handleInvoicePaid = React.useCallback(async () => {
    await loadInvoices();
  }, [loadInvoices]);

  React.useEffect(() => { loadInvoices(); }, [loadInvoices]);

  if (!userInfo) return <LoadingIndicator />;

  const openInvoices = invoices?.filter((invoice) => invoice.status === 'open') ?? [];
  const outstanding = openInvoices.reduce((sum, invoice) => sum + (invoice.amountRemaining ?? invoice.amountDue ?? 0), 0);
  const setTab = (tab) => history.push(`/dashboard/billing?tab=${tab}`);
  const activeTabLabel = activeTab === 'invoices' ? 'Invoices' : 'Payment Methods';
  const setActiveTabLabel = (tab) => setTab(tab === 'Invoices' ? 'invoices' : 'payment-methods');

  return (
    <div className={classes.root}>
      <DashboardSection
        sectionName="Billing"
        tabs={tabLabels}
        activeTab={activeTabLabel}
        setActiveTab={setActiveTabLabel}
        buttonLayout="right"
      />

      {!!openInvoices.length && (
        <Card variant="outlined" className={classes.overdue}>
          <CardContent className={classes.overdueCopy}>
            <WarningRounded />
            <div>
              <Typography variant="subtitle1">
                <b>
                  {formatCents(outstanding)}
                  {' '}
                  overdue
                </b>
              </Typography>
              <Typography variant="body2">
                {openInvoices.length}
                {' '}
                invoice
                {openInvoices.length === 1 ? '' : 's'}
                {' '}
                need attention.
              </Typography>
            </div>
          </CardContent>
          {activeTab !== 'invoices' && (
            <Box p={2}>
              <Button color="primary" round onClick={() => setTab('invoices')}>Review invoices</Button>
            </Box>
          )}
        </Card>
      )}

      {activeTab === 'payment-methods' && (
        <div>
          <div className={classes.methodsHeader}>
            <div>
              <Typography variant="body2" color="textSecondary">Your default card is automatically selected for new campaigns and digital ads.</Typography>
            </div>
            <AddPaymentMethodButton />
          </div>
          <div className={classes.methods}>
            {userInfo.agentInfo.stripe.paymentMethods.map((method) => <PaymentMethodDisplay key={method.id} paymentMethodId={method.id} brand={method.brand} last4={method.last4} expMonth={method.expMonth} expYear={method.expYear} isDefault={method.isDefault} />)}
            {!userInfo.agentInfo.stripe.paymentMethods.length && <Typography color="textSecondary" style={{ fontWeight: 500 }}>No saved payment methods yet.</Typography>}
          </div>
        </div>
      )}
      {activeTab === 'invoices' && <InvoicesTable invoices={invoices} allowRetry onRetrySuccess={handleInvoicePaid} />}
    </div>
  );
};

export default UserBillingPage;
